// @ts-strict-ignore
import { BaseLayerElementCreator } from '@3d-scene/layer/creator/base-layer-element-creator';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Dictionary } from 'typescript-collections';
import { Group, Object3D, Vector3 } from 'three';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ContainerService } from '../../model3d/container.service';
import { SimpleTextValueVO } from '@shared/vo/meta-element.vo';
import { LabelElement } from '@3d-scene/layer/elements/label-element';
import { PointerLabelElement } from '@3d-scene/layer/elements/pointer-label-element';
import { DimensionUtils } from '@3d-scene/layer/util/dimension.utils';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { UserModel } from '@user/model/user.model';
import { ProjectModel } from '@project/model/project.model';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ProjectHelper } from '@project/helper/project.helper';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

export class ArticleNumberLayerElementCreator extends BaseLayerElementCreator {
  protected static readonly OFFSET: number = 150;
  protected static readonly INITIAL_LABEL_ROTATION: Vector3 = new Vector3(-90, 0, -90);

  private _terminalBlockLabelingStartZ: number = Number.MIN_VALUE;
  private _longestTerminalBlockLabel: number = Number.MIN_VALUE;
  private _longestEndPlateLabel: number = Number.MIN_VALUE;

  constructor(
    projectModel: ProjectModel,
    private userModel: UserModel,
    private projectContentPresenter: ProjectContentPresenter,
    private mountingRailHelper: MountingRailHelper,
    private projectHelper: ProjectHelper,
    private containerService: ContainerService
  ) {
    super(projectModel, 'articleNumbers');
  }

  public createLayerElements(): Group {
    let connectedArticles: ArticleContainer[] = this.mountingRailHelper.getRailArticleContainers();
    if (ArrayUtils.isEmpty(connectedArticles)) {
      return this.createLayerGroup();
    }

    this._terminalBlockLabelingStartZ = this.findMinZValue();

    connectedArticles = connectedArticles.filter((container) => this.isIncludedInCalculation(container));

    const labelsMap: Dictionary<string, ArticleContainer> = this.collectLabels(connectedArticles);

    const layerElements: Object3D[] = labelsMap.keys().map(
      (key) => this.createLabelForContainer(labelsMap.getValue(key), key, connectedArticles));
    return this.createLayerGroup(layerElements);
  }

  private collectLabels(containers: ArticleContainer[]): Dictionary<string, ArticleContainer> {
    let sameArticle: ArticleContainer = containers[0];
    let sameArticleCount: number = 0;
    const labelsMap: Dictionary<string, ArticleContainer> = new Dictionary<string, ArticleContainer>();
    let addedGroupsCount: number = 0;
    let articlePositionIndex = 1;

    for (let i = 0; i < containers.length; i++){
      const container = containers[i];
      const compositeId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(container);
      const isLastArticle = i == containers.length - 1;
      const nextContainer: ArticleContainer = isLastArticle ? null : containers[i + 1];
      const nextCompositeId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(nextContainer);

      if (compositeId && nextCompositeId && compositeId === nextCompositeId) {
        continue;
      }
      const isEndPlate: boolean = ArticleUtils.isEndPlateArticle(container.article);
      sameArticleCount++;
      const currentArticleNo: string = this.getArticleNo(container);
      if (nextContainer == null || currentArticleNo !== this.getArticleNo(nextContainer)) {
        addedGroupsCount++;
        const text: string = addedGroupsCount + '. (#' + articlePositionIndex + ') ' + this.getArticleNumberLabel(sameArticleCount, currentArticleNo);
        this.updateLabelsLength(text, isEndPlate);
        labelsMap.setValue(text, sameArticle);
        articlePositionIndex += sameArticleCount;
        sameArticleCount = 0;
        sameArticle = nextContainer;
      }
    }
    return labelsMap;
  }

  private getArticleNumberLabel(sameArticleCount: number, currentArticleNo: string): string {
    let result: string = '';
    if (this.projectHelper.isDisplayOfProjectStructureEnabled()) {
      result += (sameArticleCount > 1 ? sameArticleCount.toString() + BaseLayerElementCreator.X_DELIMITER : '') + currentArticleNo;
    }
    return result;
  }

  private createLabelForContainer(article: ArticleContainer, text: string, articlesOnRail: ArticleContainer[]): Object3D {
    let numberElement: Object3D;
    if (ArticleUtils.isEndPlateArticle(article.article)) {
      const articlePosition: Vector3 = new Vector3(article.position.x, article.position.y, this.calculateMinGlobalArticlePoint(article).z);
      numberElement = new PointerLabelElement(
        this.getLabelPosition(article, articlesOnRail),
        articlePosition,
        this.createTextValue(text),
        this._longestEndPlateLabel
      );
    } else {
      numberElement = new LabelElement(
        this.createTextValue(text),
        LabelElement.DEFAULT_FONT_SIZE,
        ArticleNumberLayerElementCreator.INITIAL_LABEL_ROTATION,
        this._longestTerminalBlockLabel
      );
      const numberElementPosition: Vector3 = this.getLabelPosition(article, articlesOnRail);
      numberElement.position.set(numberElementPosition.x, numberElementPosition.y, numberElementPosition.z);
    }
    return numberElement;
  }

  private getArticleNo(article: ArticleContainer): string {
    let articleNo: string = null;
    if (ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(article.articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO)) {
      articleNo = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(article.articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO);
    } else if (ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(article.articleUsage, ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_NUMBER)) {
      articleNo = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(article.articleUsage, ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_NUMBER);
    } else {
      articleNo = article.article.articleNo;
    }
    if (this.projectContentPresenter.showCustomArticleNumber) {
      articleNo = this.userModel.getCustomArticleNumber(articleNo, articleNo);
    }
    return articleNo;
  }

  private getLabelPosition(article: ArticleContainer, articlesOnRail: ArticleContainer[]): Vector3 {
    let startArticleXPosition: number = this.calculateMinGlobalArticlePoint(article).x;
    const endArticleXPosition: number = this.calculateMaxGlobalArticlePoint(article).x;
    const articleIndex: number = articlesOnRail.indexOf(article);
    if (articleIndex > 0) {
      const previousArticle: ArticleContainer = articlesOnRail[articleIndex - 1];
      const maxGlobalPreviousArticlePoint: Vector3 = this.calculateMaxGlobalArticlePoint(previousArticle);
      if (maxGlobalPreviousArticlePoint.x < startArticleXPosition && maxGlobalPreviousArticlePoint.x > endArticleXPosition) {
        startArticleXPosition = maxGlobalPreviousArticlePoint.x;
      }
    }

    let articleZPosition: number = this._terminalBlockLabelingStartZ;
    if (ArticleUtils.isEndPlateArticle(article.article)) {
      articleZPosition = articleZPosition - this._longestTerminalBlockLabel + ArticleNumberLayerElementCreator.OFFSET;
    }

    return new Vector3((startArticleXPosition + endArticleXPosition) / 2, article.position.y, articleZPosition);
  }

  protected findMinZValue(): number {
    return this.calculateMinGlobalPointOfArticles(this.containerService.enumerateArticleContainersInGlobal()).z - ArticleNumberLayerElementCreator.OFFSET;
  }

  private updateLabelsLength(text: string, endPlateArticle: boolean): void {
    const labelLength: number = DimensionUtils.getTextLength(text);
    if (endPlateArticle) {
      this._longestEndPlateLabel = Math.max(this._longestEndPlateLabel, labelLength);
    } else {
      this._longestTerminalBlockLabel = Math.max(this._longestTerminalBlockLabel, labelLength);
    }
  }

  public createTextValue(value: string): SimpleTextValueVO {
    const simpleTextValue: SimpleTextValueVO = new SimpleTextValueVO();
    simpleTextValue.text = value;
    simpleTextValue.hAlignment = -1;
    simpleTextValue.vAlignment = 0;
    return simpleTextValue;
  }
}
